import type { Annotation } from "@rapid/types/annotation";
import type { PromptLabel } from "@rapid/store/slices/modelIntentSlice";
import type { InferenceStrategyConfig } from "./types";
import { SAM3_INFERENCE_CONFIDENCE_THRESHOLD } from "@rapid/utils/predictions";

/**
 * Inference strategy for the Improve flow.
 *
 * Improve flow has simpler inference rules:
 * - ALL text prompts use SAM3 (hasDiffs is irrelevant)
 * - OWLv2 is only triggered manually via "Find Similar Objects" button
 * - Box prompting only uses current image's annotations
 * - Always append predictions to preserve existing annotations
 * - Images are already on CDN, so pass URLs directly
 */
export class ImproveFlowStrategy implements InferenceStrategyConfig {
    getSam3Labels(_promptLabels: Map<string, PromptLabel>, textPrompts: string[]): string[] {
        // In Improve flow, ALL text prompts use SAM3
        // hasDiffs is irrelevant - if user typed it, they want predictions for it
        return textPrompts;
    }

    getOwlv2Labels(_promptLabels: Map<string, PromptLabel>): string[] {
        // OWLv2 is manual-only in Improve flow
        // Only triggered by "Find Similar Objects" button
        return [];
    }

    shouldAppendPredictions(_isMixedInference: boolean): boolean {
        // In Improve flow, ALWAYS append to preserve existing annotations
        return true;
    }

    getBoxPromptingAnnotations(
        _allAnnotationsForLabel: Annotation[],
        currentFileAnnotations: Annotation[]
    ): Annotation[] {
        // Improve flow only uses current image's annotations as prompts
        return currentFileAnnotations;
    }

    shouldIncludeImageUrls(): boolean {
        // Improve flow images are already on CDN (source.roboflow.one)
        // Pass URLs directly to skip storage lookup
        return true;
    }

    getTextPromptConfidenceThreshold(): number {
        return SAM3_INFERENCE_CONFIDENCE_THRESHOLD;
    }

    hasDiffsRelevant(): boolean {
        // hasDiffs is irrelevant in Improve flow
        return false;
    }
}
